import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getLocalStorageGameConfigs } from '../config/games';
import { getCentralDateString } from '../utils/centralTime';
import { logError } from '../utils/logger';
import apiService from '../services/api';

const useDailyGameState = (gameId) => {
  const { user, isAuthenticated } = useAuth();
  
  // Optional toast - only use if available (for testing compatibility)
  let addToast = null;
  try {
    const toastContext = useToast();
    addToast = toastContext.addToast;
  } catch (error) {
    // Toast context not available - that's okay for tests
  }

  // Use centralized date function directly

  const loadDailyGameState = useCallback(async (localStorageKey) => {
    if (isAuthenticated && user) {
      // For authenticated users, load from database
      try {
        const today = getCentralDateString();
        const response = await apiService.get(`/api/daily-game-states/user/${user.id}/game/${gameId}/date/${today}`);
        const data = await response.json();
        
        if (data) {
          // Transform database format to component format
          const transformedState = {
            date: today,
            currentCard: data.current_card_id ? JSON.parse(data.current_card_id) : null,
            attempts: data.attempts_data || [],
            guessedCards: data.guessed_cards || [],
            gameWon: data.game_won || false,
            isHintRevealed: data.is_hint_revealed || false
          };
          return transformedState;
        }
      } catch (error) {
        logError('Error loading daily game state from database', error);
      }
      return null;
    } else {
      // For non-authenticated users, use localStorage
      try {
        const savedState = localStorage.getItem(localStorageKey);
        if (savedState) {
          const parsed = JSON.parse(savedState);
          const today = getCentralDateString();
          
          if (parsed.date === today) {
            return parsed;
          }
        }
      } catch (error) {
        logError('Error loading daily game state from localStorage', error);
      }
      return null;
    }
  }, [isAuthenticated, user, gameId]);

  const saveDailyGameState = useCallback(async (state, localStorageKey) => {
    const today = getCentralDateString();
    
    if (isAuthenticated && user) {
      // For authenticated users, save to database
      try {
        await apiService.post(
          `/api/daily-game-states/user/${user.id}/game/${gameId}/date/${today}`,
          {
            game_won: state.gameWon || false,
            is_hint_revealed: state.isHintRevealed || false,
            current_card_id: state.currentCard ? JSON.stringify(state.currentCard) : null,
            guessed_cards: state.guessedCards || [],
            attempts_data: state.attempts || []
          }
        );
      } catch (error) {
        logError('Error saving daily game state to database', error);
        if (addToast) {
          addToast({
            type: 'error',
            message: 'Failed to save your progress to the cloud. Playing offline for now.',
            autoClose: true,
            autoCloseDelay: 6000
          });
        }
      }
    } else {
      // For non-authenticated users, use localStorage
      try {
        localStorage.setItem(localStorageKey, JSON.stringify({
          ...state,
          date: today
        }));
      } catch (error) {
        logError('Error saving daily game state to localStorage', error);
        if (addToast) {
          addToast({
            type: 'error',
            message: 'Failed to save your progress locally. Please check your browser storage.',
            autoClose: true,
            autoCloseDelay: 6000
          });
        }
      }
    }
  }, [isAuthenticated, user, gameId]);

  const getDailyCompletionStates = useCallback(async () => {
    if (isAuthenticated && user) {
      // For authenticated users, get from database
      try {
        const today = getCentralDateString();
        const response = await apiService.get(`/api/daily-game-states/user/${user.id}/date/${today}`);
        return await response.json();
      } catch (error) {
        logError('Error loading daily completion states from database', error);
        return {};
      }
    } else {
      // For non-authenticated users, check localStorage
      const today = getCentralDateString();
      const games = getLocalStorageGameConfigs();

      const completionStates = {};
      
      games.forEach(game => {
        try {
          const dailyData = localStorage.getItem(game.dailyKey);
          if (dailyData) {
            const parsed = JSON.parse(dailyData);
            if (parsed.date === today && parsed.gameWon === true) {
              completionStates[game.key] = {
                game_won: true,
                attempts_count: parsed.attempts ? parsed.attempts.length : 0
              };
            }
          }
        } catch (error) {
          logError(`Error loading daily data for ${game.key}`, error);
        }
      });

      return completionStates;
    }
  }, [isAuthenticated, user]);

  return {
    loadDailyGameState,
    saveDailyGameState,
    getDailyCompletionStates,
    isAuthenticated
  };
};

export default useDailyGameState;